import { useAuth } from '@context/auth';
import {
  Configuration,
  CoreClientType,
  ValidationError,
} from '@givz/core-client';
import { ClientResponse } from '@givz/core-client/dist/client/utils';
import { useNotificationChannel } from '@hooks/notification';
import {
  MutationFunction,
  QueryFunction,
  QueryKey,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { coreClient } from '../client';

export const useCoreClient = (props?: Configuration) => {
  const auth = useAuth();

  let config: Partial<Configuration> = {
    ...(auth?.accessToken && {
      accessToken: auth?.isImpersonating
        ? auth?.impersonatorAccessToken
        : auth?.accessToken,
    }),
  };

  if (props) {
    config = { ...config, ...props };
  }

  return () => coreClient(config as Configuration);
};

export const useOnCoreClientErrors = () => {
  const { error: notifyError } = useNotificationChannel();
  const auth = useAuth();

  const onApiRequestError = (_error?: unknown) => {
    notifyError('Something went wrong. Please try again later.');
  };

  const onApiResponseError = (errors: ValidationError[]) => {
    errors
      .filter((error) => error.code > 400)
      .forEach((error) => notifyError(error.message));

    if (errors.some((error) => [401, 403].includes(error.code))) {
      auth?.logout();
    }
  };

  return {
    onApiRequestError,
    onApiResponseError,
  };
};

export type UseCoreClientMutationParams<TData, TError, TVariables> = {
  mutationFn: (
    coreClient: CoreClientType,
  ) => MutationFunction<ClientResponse<TData>, TVariables>;
  onSuccess?: (data: TData) => void;
  onError?: (errors: ValidationError[]) => void;
} & Partial<
  Omit<
    UseMutationOptions<ClientResponse<TData>, TError, TVariables, unknown>,
    'mutationFn' | 'onSuccess' | 'onError'
  >
>;

export const useCoreClientMutation = <TData, TError, TVariables>(
  params: UseCoreClientMutationParams<TData, TError, TVariables>,
) => {
  const { mutationFn, onError, onSuccess, ...options } = params;
  const coreClient = useCoreClient();
  const { onApiRequestError, onApiResponseError } = useOnCoreClientErrors();
  const client = coreClient();

  const onSuccessResponse = (clientResponse: ClientResponse<TData>) => {
    const { data, errors } = clientResponse;

    if (errors) {
      return onError ? onError(errors) : onApiResponseError(errors);
    }

    return onSuccess ? onSuccess(data) : data;
  };

  return useMutation({
    mutationFn: mutationFn(client),
    onError: onApiRequestError,
    onSuccess: onSuccessResponse,
    ...options,
  });
};

export type UseQueryCoreClientParams<Response, TError, Key extends QueryKey> = {
  queryFn: (
    coreClient: CoreClientType,
  ) => QueryFunction<ClientResponse<Response>, Key>;
  onSuccess?: (data: Response) => void;
  onError?: (errors: ValidationError[]) => void;
} & Partial<
  Omit<
    UseQueryOptions<
      ClientResponse<Response>,
      TError,
      ClientResponse<Response>,
      Key
    >,
    'queryFn' | 'onSuccess' | 'onError'
  >
>;

export const useCoreClientQuery = <Response, TError, Key extends QueryKey>(
  params: UseQueryCoreClientParams<Response, TError, Key>,
) => {
  const { onError, onSuccess, queryFn, ...options } = params;
  const coreClient = useCoreClient();
  const { onApiRequestError, onApiResponseError } = useOnCoreClientErrors();
  const client = coreClient();

  const onSuccessResponse = (clientResponse: ClientResponse<Response>) => {
    const { data, errors } = clientResponse;

    if (errors) {
      onApiResponseError(errors);
      if (onError) {
        onError(errors);
      }
      return;
    }

    return onSuccess ? onSuccess(data) : data;
  };

  return useQuery({
    onError: onApiRequestError,
    onSuccess: onSuccessResponse,
    queryFn: queryFn(client),
    ...options,
  });
};
